import { z } from 'zod';

// ---------- Lease agreements (commercial / residential) for clients ----------

export const LEASE_KINDS = ['commercial', 'residential'] as const;
export type LeaseKind = (typeof LEASE_KINDS)[number];
export const LEASE_KIND_LABELS: Record<LeaseKind, string> = { commercial: 'Commercial', residential: 'Residential' };

/**
 * draft → sent → signed (signed copy uploaded) → expired (end date passed, set by the daily job or on read).
 * Any lease that is not expired can be terminated; signed / expired leases can be renewed into a new draft.
 */
export const LEASE_STATUSES = ['draft', 'sent', 'signed', 'expired', 'terminated'] as const;
export type LeaseStatus = (typeof LEASE_STATUSES)[number];
export const LEASE_STATUS_LABELS: Record<LeaseStatus, string> = { draft: 'Draft', sent: 'Sent', signed: 'Signed', expired: 'Expired', terminated: 'Terminated' };
/** Statuses in which the lease terms can still be edited. */
export const LEASE_EDITABLE: readonly LeaseStatus[] = ['draft', 'sent'];

/** Suggestions for "Permitted use" (free text is allowed). */
export const LEASE_PERMITTED_USES = ['Mail receipt only', 'Mail and package receipt', 'Office'] as const;

/** Attachment categories on a lease. */
export const LEASE_ATTACHMENT_CATEGORIES = { generated: 'lease_generated', signed: 'lease_signed', other: 'other' } as const;

/** Reminder windows (days before the end date) for signed leases. */
export const LEASE_REMINDER_DAYS = [30, 7] as const;

/**
 * Placeholders a lease template can use. Values come from the lease (and its client); an empty value prints as a
 * blank line to fill by hand, and anything not in this list is reported as unknown before the PDF is generated.
 */
export const LEASE_PLACEHOLDERS = [
  { key: 'lease_number', label: 'Lease number (LSE-000001)', sample: 'LSE-000042' },
  { key: 'today', label: 'Date the PDF is generated', sample: 'October 23, 2026' },
  { key: 'lease_kind', label: 'Commercial / Residential', sample: 'Commercial' },
  { key: 'client_number', label: 'Client number (CLI-000001)', sample: 'CLI-000117' },
  { key: 'landlord_name', label: 'Landlord / lessor name', sample: 'Everest Ventures Group LLC' },
  { key: 'landlord_address', label: 'Landlord / lessor address', sample: '848 Brickell Avenue, Miami, FL 33131' },
  { key: 'landlord_phone', label: 'Landlord / lessor phone', sample: '+1 305 555 0100' },
  { key: 'landlord_email', label: 'Landlord / lessor email', sample: 'leases@example.com' },
  { key: 'landlord_signer_name', label: 'Landlord signer ("By:") — optional', sample: 'Jason Smith' },
  { key: 'landlord_signer_title', label: 'Landlord signer title — optional', sample: 'Managing member' },
  { key: 'tenant_name', label: 'Tenant name', sample: 'Maria Lopez' },
  { key: 'tenant_business_name', label: 'Tenant business (LLC) name — commercial leases', sample: 'Lopez Trading LLC' },
  { key: 'tenant_signer_name', label: 'Tenant signer for the LLC ("By:")', sample: 'Maria Lopez' },
  { key: 'tenant_signer_title', label: 'Tenant signer title', sample: 'Sole member/CEO' },
  { key: 'tenant_address', label: 'Tenant address', sample: 'Calle 10 #45, Bogotá, Colombia' },
  { key: 'tenant_email', label: 'Tenant email', sample: 'maria@example.com' },
  { key: 'tenant_phone', label: 'Tenant phone', sample: '+57 300 000 0000' },
  { key: 'premises_address', label: 'Premises address', sample: '1 Elm St, Austin, TX 73301' },
  { key: 'suite', label: 'Suite / unit', sample: 'Suite 204' },
  { key: 'permitted_use', label: 'Permitted use', sample: 'Mail receipt only' },
  { key: 'start_date', label: 'Start date', sample: 'November 1, 2026' },
  { key: 'end_date', label: 'End date', sample: 'October 31, 2027' },
  { key: 'term_months', label: 'Term in months', sample: '12' },
  { key: 'monthly_rent', label: 'Monthly rent', sample: '$150.00' },
  { key: 'currency', label: 'Currency', sample: 'USD' },
  { key: 'deposit', label: 'Security deposit', sample: '$150.00' },
  { key: 'payment_due_day', label: 'Rent due day of month', sample: '1st' },
  { key: 'late_fee', label: 'Late fee', sample: '$25 after 5 days' },
  { key: 'renewal_terms', label: 'Renewal (auto-renew + notice days)', sample: 'Renews automatically unless either party gives written notice at least 30 days before the end date.' },
  { key: 'special_terms', label: 'Special terms', sample: 'None.' },
  { key: 'governing_state', label: 'Governing law state (full name)', sample: 'Texas' },
  { key: 'state_clauses', label: 'State clauses for the governing state (Admin → Lease templates → State clauses); prints nothing when none are saved', sample: '' },
] as const;
export type LeasePlaceholder = (typeof LEASE_PLACEHOLDERS)[number]['key'];
export const LEASE_PLACEHOLDER_KEYS = LEASE_PLACEHOLDERS.map((p) => p.key) as LeasePlaceholder[];
export const LEASE_SAMPLE_VALUES = Object.fromEntries(LEASE_PLACEHOLDERS.map((p) => [p.key, p.sample])) as Record<LeasePlaceholder, string>;

/** Placeholders that print nothing (no blank line, no warning) when they have no value. */
export const LEASE_OPTIONAL_PLACEHOLDERS: readonly string[] = ['state_clauses', 'landlord_signer_name', 'landlord_signer_title'];

/** What an empty value prints as (a line to complete by hand). */
export const LEASE_BLANK = '__________';
const PLACEHOLDER_RE = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;

/**
 * Fills `{{placeholders}}` in a template body. Unknown placeholders are left as written (and reported); known ones
 * with no value print as a blank line (and are reported as empty).
 */
export function renderLeaseTemplate(body: string, values: Partial<Record<LeasePlaceholder, string | null | undefined>>) {
  return fillPlaceholders(body, values, LEASE_PLACEHOLDER_KEYS, LEASE_OPTIONAL_PLACEHOLDERS);
}

/**
 * Generic `{{placeholder}}` filler shared by document templates (leases, representative contracts): keys outside
 * `known` are left as written (and reported as unknown); known keys with no value print as a blank line (reported
 * as empty) unless they are optional, in which case they print nothing.
 */
export function fillPlaceholders(body: string, values: Partial<Record<string, string | null | undefined>>, known: readonly string[], optional: readonly string[] = []) {
  const unknown = new Set<string>();
  const empty = new Set<string>();
  const used = new Set<string>();
  const text = body.replace(PLACEHOLDER_RE, (whole, raw: string) => {
    const key = raw.toLowerCase();
    if (!known.includes(key)) {
      unknown.add(raw);
      return whole;
    }
    used.add(key);
    const v = values[key];
    if (v == null || String(v).trim() === '') {
      if (optional.includes(key)) return '';
      empty.add(key);
      return LEASE_BLANK;
    }
    return String(v);
  });
  return { text, unknown: [...unknown], empty: [...empty], used: [...used] };
}

/** Whole months from the start date to the day after the end date (Jan 1 – Dec 31 = 12). */
export function leaseTermMonths(startDate: string, endDate: string): number {
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const e = new Date(Date.UTC(ey, em - 1, ed) + 86_400_000);
  const months = (e.getUTCFullYear() - sy) * 12 + (e.getUTCMonth() + 1 - sm) + (e.getUTCDate() - sd) / 30;
  return Math.max(0, Math.round(months));
}

// ---------- US states (governing law) ----------
export const US_STATES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
  DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
} as const;
export type UsStateCode = keyof typeof US_STATES;
export const US_STATE_CODES = Object.keys(US_STATES) as UsStateCode[];

/**
 * State code from a state field or a one-line US address: a 2-letter code ("TX", "Austin, TX 73301") or a full state
 * name ("Austin, Texas 73301"). Null when no state is recognisable.
 */
export function usStateCode(input: string | null | undefined): UsStateCode | null {
  if (!input) return null;
  const s = input.trim();
  const exact = s.toUpperCase();
  if ((US_STATE_CODES as string[]).includes(exact)) return exact as UsStateCode;
  const byName = US_STATE_CODES.find((c) => US_STATES[c].toLowerCase() === s.toLowerCase());
  if (byName) return byName;
  // "…, TX 73301" / "…, TX" / "… TX 73301-1234"
  const m = s.match(/(?:,|\s)\s*([A-Za-z]{2})\.?(?:\s+\d{5}(?:-\d{4})?)?\s*(?:,\s*(?:USA?|United States))?\s*$/);
  if (m && (US_STATE_CODES as string[]).includes(m[1].toUpperCase())) return m[1].toUpperCase() as UsStateCode;
  // Longest names first so "West Virginia" wins over "Virginia".
  const names = [...US_STATE_CODES].sort((a, b) => US_STATES[b].length - US_STATES[a].length);
  const lower = s.toLowerCase();
  const named = names.find((c) => new RegExp(`(^|[\\s,])${US_STATES[c].toLowerCase()}([\\s,.]|\\d|$)`).test(lower));
  return named ?? null;
}

/** Seal printed next to the landlord's signature line: none, the company seal image (settings) or "(SEAL)". */
export const LEASE_LANDLORD_SEALS = ['none', 'image', 'text'] as const;
export type LeaseLandlordSeal = (typeof LEASE_LANDLORD_SEALS)[number];
export const LEASE_LANDLORD_SEAL_LABELS: Record<LeaseLandlordSeal, string> = { none: 'None', image: 'Company seal image', text: '“(SEAL)” text' };
/** Seal next to the tenant's signature line: none, "(SEAL)", or a seal / stamp image the tenant may upload while e-signing. */
export const LEASE_TENANT_SEALS = ['none', 'text', 'upload'] as const;
export type LeaseTenantSeal = (typeof LEASE_TENANT_SEALS)[number];
export const LEASE_TENANT_SEAL_LABELS: Record<LeaseTenantSeal, string> = { none: 'None', text: '“(SEAL)” text', upload: 'Tenant uploads their seal while e-signing (optional)' };
/** Seal applied to a signature (recorded on the certificate of completion). */
export const LEASE_SEAL_APPLIED_LABELS = { company: 'company seal', text: '(SEAL)', tenant_upload: 'tenant-uploaded seal' } as const;
export type LeaseSealApplied = keyof typeof LEASE_SEAL_APPLIED_LABELS;
export const LEASE_DEFAULT_SIGNER_TITLE = 'Sole member/CEO';
/** Largest seal / stamp image a tenant can upload while signing. */
export const LEASE_SEAL_MAX_BYTES = 2 * 1024 * 1024;

/** Commercial leases are signed by an LLC: its name, the person signing for it and their title are required. */
export function leaseCommercialProblems(v: { kind?: LeaseKind | null; tenantBusinessName?: string | null; tenantSignerName?: string | null; tenantSignerTitle?: string | null }) {
  if (v.kind !== 'commercial') return [];
  const out: { field: 'tenantBusinessName' | 'tenantSignerName' | 'tenantSignerTitle'; message: string }[] = [];
  if (!v.tenantBusinessName?.trim()) out.push({ field: 'tenantBusinessName', message: 'Commercial leases need the tenant’s business (LLC) name' });
  if (!v.tenantSignerName?.trim()) out.push({ field: 'tenantSignerName', message: 'Enter who signs for the LLC' });
  if (!v.tenantSignerTitle?.trim()) out.push({ field: 'tenantSignerTitle', message: 'Enter the signer’s title (e.g. Sole member/CEO)' });
  return out;
}

/**
 * Lines printed under a party's signature line. Commercial tenants sign for their LLC:
 * "<LLC> (Tenant)", "By: <signer>", "Title: <title>". The landlord gets "By:/Title:" only when a signer title is set.
 */
export function leasePartyBlockLines(p: { role: 'landlord' | 'tenant'; name: string; businessName?: string | null; signerName?: string | null; signerTitle?: string | null }): string[] {
  const role = p.role === 'tenant' ? 'Tenant' : 'Landlord';
  const blank = '____________________';
  if (p.role === 'tenant' && p.businessName?.trim()) return [`${p.businessName.trim()} (${role})`, `By: ${p.signerName?.trim() || blank}`, `Title: ${p.signerTitle?.trim() || blank}`];
  if (p.role === 'landlord' && p.signerTitle?.trim()) return [`${p.name} (${role})`, `By: ${p.signerName?.trim() || blank}`, `Title: ${p.signerTitle.trim()}`];
  return [`${p.name} (${role})`];
}

// ---------- Template markup (shared by the PDF and the web preview) ----------

/**
 * One block of a lease document:
 * - `# ` title (centered, bold) · `## ` section heading (indented)
 * - `^ text` centered lines (consecutive lines form one single-spaced block) · `>> text` right-aligned line
 * - `1. ` numbered paragraph (level 0) · `a. ` (level 1) · `i. / ii. / iii.` (level 2) · `- ` one level below the item before it
 * - anything else: a paragraph (blank lines separate paragraphs). `**bold**` works inline everywhere.
 */
export type LeaseBlock =
  | { type: 'title'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'center'; lines: string[] }
  | { type: 'right'; lines: string[] }
  | { type: 'paragraph'; lines: string[] }
  | { type: 'item'; level: number; marker: string; text: string };

const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx'];
const ITEM_LINE = /^\s*(\d{1,3}\.|[a-z]{1,5}\.|-)\s+(.*)$/;
const CENTER_LINE = /^\s*\^(?:\s(.*))?$/;
const RIGHT_LINE = /^\s*>>(?:\s(.*))?$/;

/** Marker of a list line without its dot ("12", "b", "iii", "-"), or null when the line is not a list item. */
function itemMarker(line: string): string | null {
  if (CENTER_LINE.test(line) || RIGHT_LINE.test(line)) return null;
  const m = line.match(ITEM_LINE);
  if (!m) return null;
  if (m[1] === '-') return '-';
  const mk = m[1].slice(0, -1);
  if (/^\d+$/.test(mk) || mk.length === 1 || ROMAN.includes(mk)) return mk;
  return null;
}

export function parseLeaseMarkup(text: string): LeaseBlock[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const markers = lines.map(itemMarker);
  const blocks: LeaseBlock[] = [];
  let open: LeaseBlock | null = null;
  let lastLetter: string | null = null;
  let lastRoman: string | null = null;
  let lastLevel = -1;
  // "i." is roman under "h." when "ii." follows, and the letter i when "j." follows (or after "h." otherwise).
  const isRoman = (mk: string, idx: number) => {
    if (mk.length > 1) return true;
    const next = markers.slice(idx + 1).find((x) => x && x !== '-') ?? null;
    if (mk === 'i') return next === 'ii' || (next !== 'j' && lastLetter !== 'h');
    if (mk === 'v') return lastRoman === 'iv';
    if (mk === 'x') return lastRoman === 'ix';
    return false;
  };
  const add = (b: LeaseBlock) => {
    blocks.push(b);
    open = b;
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const cur = open as LeaseBlock | null;
    if (line.trim() === '') {
      open = null;
      return;
    }
    if (line.startsWith('# ') || line.startsWith('## ')) {
      blocks.push(line.startsWith('# ') ? { type: 'title', text: line.slice(2).trim() } : { type: 'heading', text: line.slice(3).trim() });
      open = null;
      lastLevel = -1;
      return;
    }
    const c = line.match(CENTER_LINE);
    if (c) {
      if (cur?.type === 'center') cur.lines.push((c[1] ?? '').trim());
      else add({ type: 'center', lines: [(c[1] ?? '').trim()] });
      return;
    }
    const r = line.match(RIGHT_LINE);
    if (r) {
      if (cur?.type === 'right') cur.lines.push((r[1] ?? '').trim());
      else add({ type: 'right', lines: [(r[1] ?? '').trim()] });
      return;
    }
    const mk = markers[idx];
    if (mk !== null) {
      const m = line.match(ITEM_LINE)!;
      let level: number;
      if (mk === '-') level = Math.min(lastLevel + 1, 4);
      else if (/^\d+$/.test(mk)) {
        level = 0;
        lastLetter = lastRoman = null;
      } else if (isRoman(mk, idx)) {
        level = 2;
        lastRoman = mk;
      } else {
        level = 1;
        lastLetter = mk;
        lastRoman = null;
      }
      if (mk !== '-') lastLevel = level;
      add({ type: 'item', level, marker: m[1], text: m[2].trim() });
      return;
    }
    if (cur?.type === 'paragraph') cur.lines.push(line.trim());
    else add({ type: 'paragraph', lines: [line.trim()] });
    lastLevel = -1;
  });
  return blocks;
}

/** `**bold**` runs of a line (an unmatched `**` prints as written). */
export function parseLeaseInline(text: string): { text: string; bold: boolean }[] {
  const out: { text: string; bold: boolean }[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let at = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > at) out.push({ text: text.slice(at, m.index), bold: false });
    out.push({ text: m[1], bold: true });
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push({ text: text.slice(at), bold: false });
  return out.length ? out : [{ text: '', bold: false }];
}

const text = (max = 2000) => z.string().trim().max(max);
const optText = (max = 2000) => text(max).nullish().transform((v) => (v ? v : null));
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const cents = z.number().int().min(0).max(100_000_000);

const leaseFields = z.object({
  clientId: z.string().uuid(),
  kind: z.enum(LEASE_KINDS),
  landlordName: text(200).min(1, 'Required'),
  landlordAddress: optText(500),
  landlordPhone: optText(60),
  landlordEmail: z.string().trim().email().max(200).nullish().or(z.literal('')).transform((v) => (v ? v : null)),
  /** Governing law; when empty it is taken from the premises state, else from the default landlord's state. */
  governingState: z.enum(US_STATE_CODES as [UsStateCode, ...UsStateCode[]]).nullish(),
  /** Optional "By: / Title:" under the landlord's signature line. */
  landlordSignerName: optText(200),
  landlordSignerTitle: optText(200),
  tenantName: text(200).min(1, 'Required'),
  /** Commercial leases: the tenant is an LLC signed for by a person with a title (required for commercial leases). */
  tenantBusinessName: optText(200),
  tenantSignerName: optText(200),
  tenantSignerTitle: optText(200),
  /** Seals next to the signature lines; the landlord default comes from the default-landlord settings. */
  landlordSeal: z.enum(LEASE_LANDLORD_SEALS).optional(),
  tenantSeal: z.enum(LEASE_TENANT_SEALS).optional(),
  /** "(Witness)" lines beside each party's signature block (default on for commercial leases). */
  witnessLines: z.boolean().optional(),
  tenantAddress: optText(500),
  tenantEmail: z.string().trim().email().max(200).nullish().or(z.literal('')).transform((v) => (v ? v : null)),
  tenantPhone: optText(60),
  /** A portal address (address inventory); its address is copied into the premises address when that is empty. */
  addressId: z.string().uuid().nullish(),
  premisesAddress: optText(500),
  suite: optText(100),
  permittedUse: optText(300),
  startDate: dateOnly,
  endDate: dateOnly,
  monthlyRentCents: cents.default(0),
  currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code').default('USD'),
  depositCents: cents.default(0),
  paymentDueDay: z.number().int().min(1).max(31).nullish(),
  lateFee: optText(300),
  autoRenew: z.boolean().default(false),
  renewalNoticeDays: z.number().int().min(0).max(365).nullish(),
  specialTerms: optText(10_000),
});

export const leaseInput = leaseFields
  .refine((v) => v.endDate >= v.startDate, { message: 'The end date must be on or after the start date', path: ['endDate'] })
  .superRefine((v, ctx) => {
    for (const p of leaseCommercialProblems(v)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: p.message, path: [p.field] });
  });
export type LeaseInput = z.infer<typeof leaseInput>;
export const leaseUpdateInput = leaseFields.omit({ clientId: true }).partial();
export type LeaseUpdateInput = z.infer<typeof leaseUpdateInput>;

export const leaseTemplateInput = z.object({
  kind: z.enum(LEASE_KINDS),
  name: text(200).min(1, 'Required'),
  body: z.string().max(100_000).refine((v) => v.trim().length > 0, 'Write the template text'),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
});
export type LeaseTemplateInput = z.infer<typeof leaseTemplateInput>;

/** Landlord / lessor printed on new leases by default (Admin → Lease templates). */
export const LEASE_ADDRESS_LANDLORDS = ['company', 'representative'] as const;
export type LeaseAddressLandlord = (typeof LEASE_ADDRESS_LANDLORDS)[number];
export const leaseSettingsInput = z.object({
  landlordName: text(200),
  landlordAddress: text(500),
  landlordPhone: text(60).default(''),
  landlordEmail: z.string().trim().email().max(200).or(z.literal('')).default(''),
  /** Fallback governing state when the premises state is unknown (2-letter code, or empty). */
  landlordState: z.enum(US_STATE_CODES as [UsStateCode, ...UsStateCode[]]).or(z.literal('')).default(''),
  /** Who is the landlord on leases created from an address assignment. */
  addressLeaseLandlord: z.enum(LEASE_ADDRESS_LANDLORDS).default('company'),
  /** Create a draft lease automatically when a client is assigned to an address (single assignments). */
  autoDraftOnAssign: z.boolean().default(false),
  /** Optional "By: / Title:" printed under the landlord's signature line. */
  landlordSignerName: text(200).default(''),
  landlordSignerTitle: text(200).default(''),
  /** New leases get the company seal image (uploaded separately) next to the landlord's signature. */
  applySealByDefault: z.boolean().default(false),
});
export type LeaseSettings = z.infer<typeof leaseSettingsInput>;

/** Optional state-specific clauses / disclosures (from the owner's lawyer), added through {{state_clauses}}. */
export const leaseStateClauseInput = z.object({ body: z.string().max(50_000) });

// ---------- Built-in e-signing (no third-party provider) ----------

export const LEASE_SIGNATURE_REQUEST_STATUSES = ['pending', 'completed', 'declined', 'voided'] as const;
export type LeaseSignatureRequestStatus = (typeof LEASE_SIGNATURE_REQUEST_STATUSES)[number];
export const LEASE_SIGNER_ROLES = ['tenant', 'landlord'] as const;
export type LeaseSignerRole = (typeof LEASE_SIGNER_ROLES)[number];
export const LEASE_SIGNER_STATUSES = ['pending', 'viewed', 'signed', 'declined'] as const;
export type LeaseSignerStatus = (typeof LEASE_SIGNER_STATUSES)[number];
/** Events shown in the lease history and on the certificate of completion. */
export const LEASE_SIGNATURE_EVENTS = ['sent', 'link_created', 'link_emailed', 'link_whatsapp', 'link_revoked', 'viewed', 'signed', 'declined', 'expired', 'voided', 'completed'] as const;
export type LeaseSignatureEvent = (typeof LEASE_SIGNATURE_EVENTS)[number];
/** Signing links stay valid this long (each new link restarts the clock). */
export const LEASE_SIGN_LINK_DAYS = 14;

/** Consent shown above the signature (its version is recorded with every signature). */
export const LEASE_ESIGN_CONSENT = {
  version: 'esign-consent-2026-10',
  text: 'I agree to sign this document electronically and that my electronic signature is legally binding, the same as a handwritten signature, under the U.S. ESIGN Act and the Uniform Electronic Transactions Act (UETA). I have been able to review and download the document before signing, and I can ask for a paper copy.',
} as const;

const optEmail = z.string().trim().email().max(200).nullish().or(z.literal('')).transform((v) => (v ? v : null));
const signerContact = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  email: optEmail,
  phone: z.string().trim().max(60).nullish().transform((v) => (v ? v : null)),
});

export const leaseSignatureRequestInput = z.object({
  tenant: signerContact,
  /** The landlord signs in the portal (a user with lease.write, default the sender) or by link when the landlord is not us. */
  landlord: z.discriminatedUnion('type', [
    z.object({ type: z.literal('user'), userId: z.string().uuid().nullish() }),
    z.object({ type: z.literal('external'), name: z.string().trim().min(1, 'Required').max(200), email: optEmail, phone: z.string().trim().max(60).nullish() }),
  ]),
  landlordFirst: z.boolean().default(false),
});
export type LeaseSignatureRequestInput = z.infer<typeof leaseSignatureRequestInput>;

/** A PNG data URL produced by the signature pad (kept small). */
const pngDataUrl = z.string().max(700_000).regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'The drawn signature must be a PNG image');

export const leaseSignInput = z
  .object({
    consent: z.literal(true, { errorMap: () => ({ message: 'Agree to sign electronically first' }) }),
    consentVersion: z.string().max(60).optional(),
    legalName: z.string().trim().min(2, 'Type your full legal name').max(200),
    method: z.enum(['typed', 'drawn']),
    signatureText: z.string().trim().max(200).nullish(),
    signatureImage: pngDataUrl.nullish(),
    initials: z.string().trim().max(10).nullish(),
    /** Optional name of a witness, printed under the "(Witness)" line (no witness signature is collected). */
    witnessName: z.string().trim().max(200).nullish().transform((v) => (v ? v : null)),
    /** Optional seal / stamp image (PNG or JPG, at most 2 MB) when the lease lets the tenant upload one. */
    sealImage: z
      .string()
      .max(Math.ceil((LEASE_SEAL_MAX_BYTES * 4) / 3) + 40, 'The seal image must be 2 MB or smaller')
      .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, 'The seal must be a PNG or JPG image')
      .nullish(),
  })
  .refine((v) => v.method !== 'drawn' || !!v.signatureImage, { message: 'Draw your signature', path: ['signatureImage'] });
export type LeaseSignInput = z.infer<typeof leaseSignInput>;

export const leaseSignLinkInput = z.object({
  /** Empty: only create a link to copy. */
  channels: z.array(z.enum(['email', 'whatsapp'])).max(2).default([]),
  message: z.string().trim().max(1000).nullish(),
});
